import InfoIcon from "@mui/icons-material/Info";
import Accordion from "@mui/joy/Accordion";
import AccordionDetails from "@mui/joy/AccordionDetails";
import AccordionGroup from "@mui/joy/AccordionGroup";
import AccordionSummary from "@mui/joy/AccordionSummary";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Sheet from "@mui/joy/Sheet";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import React from "react";
import { Fact, FactId, FactQualifier } from "../../../../models/fact";
import {
  ApproximateTime,
  ExactTime,
  RelativeTime,
  TimeInterval,
} from "../../../../models/human_time";
import { CodeableConcept, Coding, Quantity } from "../../../../models/structuration";
import { capitalizeFirstLetter, getDomain, getDomainColor } from "../../../../shared/utils/fact";
import { generateNoteRelativeTime } from "../../../../shared/utils/time";
import { useNote } from "../../contexts";
import { Label } from "../../providers/NoteProvider";
import { FactRenderer } from "./FactDisplayAndEdit/FactRenderer";
import { QualitfierEdit } from "./FactDisplayAndEdit/Qualifiers/QualifierEdit";
import { TemporalityEdit } from "./FactDisplayAndEdit/TemporalityEdit";
import { SearchBar } from "../../../../shared/components/search/SearchBar";

const groupFactsByDomain = (facts: Array<Fact>) => {
  const grouped: { [key: string]: typeof facts } = {};

  facts.forEach((fact) => {
    const domain = getDomain(fact.code);

    if (!domain || domain === "Patient" || domain === "Document" || domain === "PII") {
      return;
    }

    if (grouped[domain]) {
      grouped[domain].push(fact);
    } else {
      grouped[domain] = [fact];
    }
  });

  // Sort domains alphabetically
  const sortedEntries = Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
  return Object.fromEntries(sortedEntries);
};

type TimeValue = ApproximateTime | ExactTime | RelativeTime | TimeInterval | null;

interface BulkEditionSheetProps {
  readonly displayedFacts: Array<Fact>;
  readonly onBulkDateChange: (newDate: TimeValue) => void;
  readonly onBulkQualifierChange: (factQualifier: FactQualifier) => void;
  readonly onBulkDelete: () => void;
}

function BulkEditionSheet({
  displayedFacts,
  onBulkDateChange,
  onBulkQualifierChange,
  onBulkDelete,
}: BulkEditionSheetProps) {
  // Use the first fact's date as the initial state, or null.
  const initialDate = displayedFacts.length > 0 ? displayedFacts[0].time : null;
  const initialQualifier = displayedFacts.length > 0 ? displayedFacts[0].qualifier : null;
  const [bulkDate, setBulkDate] = React.useState(initialDate);
  const [bulkQualifier, setBulkQualifier] = React.useState(initialQualifier);

  const handleDateChange = (
    newDate: ApproximateTime | ExactTime | RelativeTime | TimeInterval | null
  ) => {
    setBulkDate(newDate);
    onBulkDateChange(newDate);
  };

  const handleQualifierChange = (factQualifier: FactQualifier) => {
    setBulkQualifier(factQualifier);
    onBulkQualifierChange(factQualifier);
  };

  return (
    <Stack direction="column" spacing={2}>
      <hr />
      <Typography level="h4">Bulk edition</Typography>
      <TemporalityEdit time={bulkDate} onChange={handleDateChange} />
      <QualitfierEdit factQualifier={bulkQualifier} onChange={handleQualifierChange} />
      <Stack direction="row" spacing={2}>
        <Button
          variant="solid"
          color="danger"
          onClick={(event) => {
            event.stopPropagation(); // Prevent triggering other click handlers.
            onBulkDelete();
          }}
        >
          Delete All
        </Button>
      </Stack>
    </Stack>
  );
}

export function FactList() {
  const {
    note,
    selectedLabels,
    selectedFact,
    displayedFacts,
    setNote,
    setSelectedFact,
    setDisplayAllFacts,
    toggleSelectedLabel,
  } = useNote();

  const [isFactEditable, setIsFactEditable] = React.useState(false);
  const [displayAnnotations, setDisplayAnnotations] = React.useState(false);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setDisplayAllFacts();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [setDisplayAllFacts]);

  React.useEffect(() => {
    if (selectedFact) {
      if (!isFactEditable || !displayAnnotations) {
        setIsFactEditable(true);
        setDisplayAnnotations(true);
      }
    } else if (selectedLabels.length > 0) {
      if (!isFactEditable || displayAnnotations) {
        setIsFactEditable(true);
        setDisplayAnnotations(false);
      }
    } else if (isFactEditable || displayAnnotations) {
      setIsFactEditable(false);
      setDisplayAnnotations(false);
    }
  }, [selectedFact, selectedLabels, isFactEditable, displayAnnotations]);

  function handleAddFact(coding: Coding) {
    if (note && selectedLabels.length === 1) {
      const factTime =
        displayedFacts.length > 0 ? displayedFacts[0].time : generateNoteRelativeTime();
      const newFact: Fact = {
        fact_id: "" as FactId,
        code: { coding: [coding] } as CodeableConcept,
        probability: 1,
        time: factTime,
        // Include all selected annotations
        source: selectedLabels.map((label) => label.annotation),
      };
      setNote({
        ...note,
        facts: [...note.facts, newFact],
      });
    }
  }

  function handleRemoveFact(factId: FactId) {
    if (note) {
      const updatedFacts = note.facts.filter((fact) => fact.fact_id !== factId);

      setNote({
        ...note,
        facts: updatedFacts,
      });

      if (selectedFact) setDisplayAllFacts();
      if (selectedLabels.length === 1) {
        const pendingLabel: Label = {
          annotation: selectedLabels[0].annotation,
          color: "#00A8E8",
        };
        toggleSelectedLabel(pendingLabel, false);
      }
    }
  }

  function handleRemoveAllFacts() {
    if (note && displayedFacts.length > 0) {
      // Gather all fact IDs that are currently displayed
      const displayedFactIds = displayedFacts.map((fact) => fact.fact_id);
      // Filter out all these facts from the note
      const updatedFacts = note.facts.filter((fact) => !displayedFactIds.includes(fact.fact_id));

      setNote({
        ...note,
        facts: updatedFacts,
      });

      setDisplayAllFacts();
    }
  }

  function handleRemoveAnnotation(factId: FactId, offset: number) {
    if (note && factId) {
      const factToUpdate = note.facts.find((fact) => fact.fact_id === factId);

      if (factToUpdate) {
        const updatedSources = factToUpdate.source.filter(
          (sourceItem) => sourceItem.offset !== offset
        );

        const updatedFact = {
          ...factToUpdate,
          source: updatedSources,
        };

        const updatedFacts = note.facts.map((fact) =>
          fact.fact_id === updatedFact.fact_id ? updatedFact : fact
        );

        setNote({
          ...note,
          facts: updatedFacts,
        });

        if (updatedSources.length === 0) {
          setDisplayAllFacts();
        }
      }
    }
  }

  // Helper for updating facts
  const updateFacts = React.useCallback(
    (updater: (fact: Fact) => Fact) => {
      if (note && displayedFacts.length > 0) {
        const updatedDisplayedFacts = displayedFacts.map(updater);
        const updatedFacts = note.facts.map(
          (fact) =>
            updatedDisplayedFacts.find((dispFact) => dispFact.fact_id === fact.fact_id) || fact
        );
        setNote({ ...note, facts: updatedFacts });
      }
    },
    [note, displayedFacts, setNote]
  );

  function handleDateChange(
    newDate: ApproximateTime | ExactTime | RelativeTime | TimeInterval | null
  ) {
    updateFacts((fact) => ({ ...fact, time: newDate }));
  }

  function handleQualifierChange(factQualifier: FactQualifier) {
    updateFacts((fact) => ({ ...fact, qualifier: factQualifier }));
  }

  function handleValueChange(factId: FactId, value: string | Quantity | CodeableConcept | null) {
    if (note && displayedFacts.length > 0) {
      const updatedDisplayedFacts = displayedFacts.map((fact) => {
        if (fact.fact_id === factId) {
          return {
            ...fact,
            value: value,
          };
        }
        return fact;
      });

      const updatedFacts = note.facts.map((fact) => {
        const displayedFact = updatedDisplayedFacts.find(
          (dispFact) => dispFact.fact_id === fact.fact_id
        );
        return displayedFact || fact;
      });

      setNote({
        ...note,
        facts: updatedFacts,
      });
    }
  }

  function handleCodeChange(newCode: CodeableConcept, factId: FactId) {
    if (note && displayedFacts.length > 0) {
      const updatedDisplayedFacts = displayedFacts.map((fact) => {
        if (fact.fact_id === factId) {
          return {
            ...fact,
            code: newCode,
          };
        }
        return fact;
      });

      const updatedFacts = note.facts.map((fact) => {
        const displayedFact = updatedDisplayedFacts.find(
          (dispFact) => dispFact.fact_id === fact.fact_id
        );
        return displayedFact || fact;
      });

      setNote({
        ...note,
        facts: updatedFacts,
      });
    }
  }

  return (
    <Stack direction="column" spacing={2}>
      {selectedLabels.length === 1 &&
        selectedLabels.every(
          (label) =>
            label.domain?.toLowerCase() !== "document" &&
            label.domain?.toLowerCase() !== "pii" &&
            label.domain?.toLowerCase() !== "patient"
        ) && (
          <Box sx={{ mb: 2 }} data-cy="add-fact-search-bar">
            <SearchBar
              onChange={(coding) => coding && handleAddFact(coding)}
              textHelper={
                selectedLabels.length > 0 &&
                (displayedFacts.length > 1 || displayedFacts.length === 0)
              }
            />
          </Box>
        )}
      {isFactEditable && displayedFacts.length > 1 && (
        <Sheet variant="outlined" sx={{ p: 2, borderRadius: "sm" }}>
          <Stack direction="row" gap={1}>
            <InfoIcon sx={{ fontSize: "18px", color: (theme) => theme.palette.grey[700] }} />
            <Typography color="neutral" level="title-sm">
              Multiple facts linked to this label, select one to edit
            </Typography>
          </Stack>
        </Sheet>
      )}
      {!(selectedFact || selectedLabels.length > 0) ? (
        <AccordionGroup variant="plain" transition="0.2s ease">
          {Object.entries(groupFactsByDomain(displayedFacts)).map(([domain, facts]) => (
            <Accordion key={domain}>
              <AccordionSummary>
                <Typography level="title-lg" sx={{ color: getDomainColor(domain) }}>
                  {capitalizeFirstLetter(domain.replace("-", " "))} ({facts.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FactRenderer
                  facts={facts}
                  domain={domain}
                  isFactEditable={isFactEditable}
                  displayAnnotations={displayAnnotations}
                  displayedFactsLength={displayedFacts.length}
                  onFactClick={setSelectedFact}
                  onRemoveFact={handleRemoveFact}
                  onValueChange={handleValueChange}
                  onCodeChange={handleCodeChange}
                  onRemoveAnnotation={handleRemoveAnnotation}
                />
              </AccordionDetails>
            </Accordion>
          ))}
        </AccordionGroup>
      ) : (
        <Box>
          {Object.entries(groupFactsByDomain(displayedFacts)).map(([domain, facts]) => (
            <FactRenderer
              key={domain}
              facts={facts}
              domain={domain}
              isFactEditable={isFactEditable}
              displayAnnotations={displayAnnotations}
              displayedFactsLength={displayedFacts.length}
              onFactClick={setSelectedFact}
              onRemoveFact={handleRemoveFact}
              onValueChange={handleValueChange}
              onCodeChange={handleCodeChange}
              onRemoveAnnotation={handleRemoveAnnotation}
            />
          ))}
        </Box>
      )}
      {isFactEditable && displayedFacts.length > 1 && (
        <BulkEditionSheet
          displayedFacts={displayedFacts}
          onBulkDateChange={handleDateChange}
          onBulkQualifierChange={handleQualifierChange}
          onBulkDelete={handleRemoveAllFacts}
        />
      )}
      {isFactEditable && displayedFacts.length === 1 && (
        <Stack direction="column" spacing={2} key={displayedFacts[0].fact_id}>
          <TemporalityEdit
            time={displayedFacts.length > 0 ? displayedFacts[0].time : null}
            onChange={handleDateChange}
          />
          <QualitfierEdit
            factQualifier={displayedFacts.length > 0 ? displayedFacts[0].qualifier : null}
            onChange={handleQualifierChange}
          />
        </Stack>
      )}
    </Stack>
  );
}
